import React, { useState, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { ConciergeMode } from '../types/communication.types'
import {
  CONCIERGE_MODE_LABELS,
  CONCIERGE_MODE_DESCRIPTIONS,
  CONCIERGE_MODE_ICONS,
  CONCIERGE_MODE_COLORS
} from '../services/conciergeModeService'

interface ConciergeModeSelectorProps {
  currentMode?: ConciergeMode
  onChange: (mode: ConciergeMode) => Promise<void>
  disabled?: boolean
}

export const ConciergeModeSelector: React.FC<ConciergeModeSelectorProps> = ({
  currentMode = 'manual',
  onChange,
  disabled = false
}) => {
  const [isChanging, setIsChanging] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })

  // 🔍 DEBUG: Monitorar mudanças no currentMode
  useEffect(() => {
    console.log('🔍 [ConciergeModeSelector] currentMode atualizado:', currentMode)
  }, [currentMode])

  // Calcular posição do dropdown quando abrir
  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
  }, [showDropdown])

  const handleModeChange = async (mode: ConciergeMode) => {
    if (mode === currentMode || isChanging) return

    console.log('🔄 [ConciergeModeSelector] Alterando modo:', { from: currentMode, to: mode })
    
    setIsChanging(true)
    try {
      await onChange(mode)
      console.log('✅ [ConciergeModeSelector] Modo alterado com sucesso:', mode)
      setShowDropdown(false)
    } catch (error) {
      console.error('❌ [ConciergeModeSelector] Erro ao alterar modo:', error)
    } finally {
      setIsChanging(false)
    }
  }

  const modes: ConciergeMode[] = ['manual', 'assisted', 'autopilot']

  // Renderizar dropdown usando portal
  const renderDropdown = () => {
    if (!showDropdown || disabled || isChanging) return null

    const dropdownContent = (
      <>
        {/* Overlay para fechar o dropdown */}
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 9998 }}
          onClick={() => setShowDropdown(false)}
        />

        {/* Menu dropdown - renderizado como portal */}
        <div
          className="position-absolute card shadow-lg"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 9999
          }}
        >
          <div className="card-body p-2">
            {modes.map((mode) => (
              <button
                key={mode}
                className={`btn btn-sm w-100 text-start mb-1 ${
                  mode === currentMode ? 'active' : ''
                }`}
                style={{
                  backgroundColor: mode === currentMode 
                    ? CONCIERGE_MODE_COLORS[mode] + '25'
                    : 'transparent',
                  border: mode === currentMode
                    ? `1px solid ${CONCIERGE_MODE_COLORS[mode]}`
                    : '1px solid transparent',
                  color: mode === currentMode
                    ? CONCIERGE_MODE_COLORS[mode]
                    : '#7E8299',
                  fontSize: '11px',
                  padding: '6px 8px'
                }}
                onClick={() => handleModeChange(mode)}
                title={CONCIERGE_MODE_DESCRIPTIONS[mode]}
              >
                <div className="d-flex align-items-start">
                  <i 
                    className={`fas ${CONCIERGE_MODE_ICONS[mode]} me-2 mt-1`} 
                    style={{ 
                      fontSize: '12px',
                      color: CONCIERGE_MODE_COLORS[mode]
                    }}
                  ></i>
                  <div className="flex-grow-1">
                    <div className="fw-semibold mb-0">{CONCIERGE_MODE_LABELS[mode]}</div>
                    <div 
                      className="text-muted" 
                      style={{ fontSize: '9px', lineHeight: '1.2' }}
                    >
                      {CONCIERGE_MODE_DESCRIPTIONS[mode]}
                    </div>
                  </div>
                  {mode === currentMode && (
                    <i className="fas fa-check ms-1" style={{ fontSize: '10px' }}></i>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </>
    )

    return ReactDOM.createPortal(dropdownContent, document.body)
  }

  return (
    <div className="concierge-mode-selector position-relative">
      {/* Botão atual */}
      <button
        ref={buttonRef}
        className="btn btn-sm w-100 text-start d-flex align-items-center justify-content-between"
        style={{
          backgroundColor: CONCIERGE_MODE_COLORS[currentMode] + '15',
          border: `1px solid ${CONCIERGE_MODE_COLORS[currentMode]}`,
          color: CONCIERGE_MODE_COLORS[currentMode],
          fontSize: '11px',
          padding: '4px 8px'
        }}
        onClick={() => setShowDropdown(!showDropdown)}
        disabled={disabled || isChanging}
        title={CONCIERGE_MODE_DESCRIPTIONS[currentMode]}
      >
        <div className="d-flex align-items-center">
          <i className={`fas ${CONCIERGE_MODE_ICONS[currentMode]} me-1`} style={{ fontSize: '10px' }}></i>
          <span className="fw-semibold">{CONCIERGE_MODE_LABELS[currentMode]}</span>
        </div>
        {isChanging ? (
          <span className="spinner-border spinner-border-sm" style={{ width: '12px', height: '12px' }}></span>
        ) : (
          <i className="fas fa-chevron-down" style={{ fontSize: '10px' }}></i>
        )}
      </button>

      {/* Renderizar dropdown usando portal */}
      {renderDropdown()}

      <style>
        {`
          .concierge-mode-selector button:not(:disabled):hover {
            opacity: 0.9;
          }
        `}
      </style>
    </div>
  )
}
